import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useSearchParams, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import EmailPopup from "./components/EmailPopup";
import VisitCounter from "./components/VisitCounter";
import FloatingAssistant from "./components/FloatingAssistant";
import InstallPWA from "./components/InstallPWA";
import LandingPage from "./pages/LandingPage";
import Dashboard from "./pages/Dashboard";
import Toolkit from "./pages/Toolkit";
import ChatBot from "./pages/ChatBot";
import Education from "./pages/Education";
import News from "./pages/News";
import Forum from "./pages/Forum";
import Pricing from "./pages/Pricing";
import PaymentSuccess from "./pages/PaymentSuccess";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Profile from "./pages/Profile";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import SecurityDashboard from "./pages/SecurityDashboard";
import PremiumServices from "./pages/PremiumServices";
import AboutUs from "./pages/AboutUs";
import Contact from "./pages/Contact";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import CyberShield from "./pages/CyberShield";
import ProtectedRoute from "./components/ProtectedRoute";
import "@/App.css";

function AppContent() {
  const { user } = useAuth();
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Check if user has already subscribed
    const hasSubscribed = localStorage.getItem('privacyshield_subscribed');
    const subscribeParam = searchParams.get('subscribe');
    
    if (subscribeParam === 'true') {
      setShowEmailPopup(true);
      return;
    }
    
    // Show popup after 30 seconds on landing page if not subscribed
    if (location.pathname === '/' && !hasSubscribed) {
      const timer = setTimeout(() => {
        setShowEmailPopup(true);
      }, 30000);
      
      return () => clearTimeout(timer);
    }
  }, [location.pathname, searchParams]);

  // Don't show floating assistant on chat page (they have full chat there)
  const showFloatingAssistant = location.pathname !== '/chat';

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      <Navbar />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/payment/success" element={
            <ProtectedRoute><PaymentSuccess /></ProtectedRoute>
          } />
          <Route path="/dashboard" element={
            <ProtectedRoute><Dashboard /></ProtectedRoute>
          } />
          <Route path="/toolkit" element={
            <ProtectedRoute><Toolkit /></ProtectedRoute>
          } />
          <Route path="/chat" element={
            <ProtectedRoute><ChatBot /></ProtectedRoute>
          } />
          <Route path="/education" element={
            <ProtectedRoute><Education /></ProtectedRoute>
          } />
          <Route path="/security" element={
            <ProtectedRoute><SecurityDashboard /></ProtectedRoute>
          } />
          <Route path="/premium" element={
            <ProtectedRoute><PremiumServices /></ProtectedRoute>
          } />
          <Route path="/news" element={<News />} />
          <Route path="/forum" element={<Forum />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/profile" element={
            <ProtectedRoute><Profile /></ProtectedRoute>
          } />
          {/* Static Pages */}
          <Route path="/about" element={<AboutUs />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/cybershield" element={
            <ProtectedRoute><CyberShield /></ProtectedRoute>
          } />
        </Routes>
      </main>
      <Footer />
      
      {/* Discreet Visit Counter */}
      <VisitCounter />
      
      {/* Floating AI Assistant */}
      {showFloatingAssistant && <FloatingAssistant user={user} />}
      
      {/* PWA Install Prompt */}
      <InstallPWA />
      
      {/* Email Capture Popup */}
      {showEmailPopup && (
        <EmailPopup onClose={() => setShowEmailPopup(false)} />
      )}
      
      <Toaster 
        position="top-right" 
        theme="dark"
        toastOptions={{
          style: {
            background: '#0a0a0a',
            border: '1px solid #262626',
            color: '#fff',
          },
        }}
      />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
